'use client'

import * as React from 'react'
import { Smartphone, TrendingUp, Info } from 'lucide-react'

// Social checkout-leak calculator. A free, interactive what-if tool.
//
// Content-integrity note: the one speculative input (how much checkout the
// native-routing handoff recovers) is a USER-SET lever, not an Idukki claim.
// The output is explicitly an indicative model, with the working shown, so
// nothing here asserts a measured result. Baseline friction figures are
// cited to public sources on the page, not invented here.

const usd0 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

function Field({
  label,
  hint,
  children,
  value,
}: {
  label: string
  hint?: string
  value: string
  children: React.ReactNode
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <label className="text-sm font-medium text-foreground">{label}</label>
        <span className="font-display text-sm font-semibold tabular-nums text-primary">{value}</span>
      </div>
      {children}
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

const sliderCls =
  'mt-2 w-full cursor-pointer accent-primary'

export function CheckoutLeakCalculator() {
  const [sessions, setSessions] = React.useState(50000)
  const [mobile, setMobile] = React.useState(80)
  const [cr, setCr] = React.useState(2.2)
  const [aov, setAov] = React.useState(60)
  const [lever, setLever] = React.useState(6)

  const mobileSessions = sessions * (mobile / 100)
  const baselineOrders = mobileSessions * (cr / 100)
  const recoveredOrders = baselineOrders * (lever / 100)
  const recoveredMonthly = recoveredOrders * aov
  const recoveredAnnual = recoveredMonthly * 12

  return (
    <div className="grid gap-6 rounded-3xl border bg-card p-6 shadow-2xl shadow-primary/5 md:grid-cols-[1.1fr_0.9fr] md:p-8">
      {/* Inputs */}
      <div className="space-y-5">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Smartphone className="h-4 w-4 text-primary" /> Your social traffic
        </div>

        <Field label="Monthly social-sourced sessions" value={mobileSessions === 0 ? '0' : sessions.toLocaleString()}>
          <input
            type="range" min={1000} max={500000} step={1000}
            value={sessions} onChange={(e) => setSessions(+e.target.value)}
            className={sliderCls} aria-label="Monthly social-sourced sessions"
          />
        </Field>

        <Field label="Mobile share" hint="Where the in-app browser dominates." value={`${mobile}%`}>
          <input
            type="range" min={0} max={100} step={1}
            value={mobile} onChange={(e) => setMobile(+e.target.value)}
            className={sliderCls} aria-label="Mobile share"
          />
        </Field>

        <Field label="Baseline conversion rate" value={`${cr.toFixed(1)}%`}>
          <input
            type="range" min={0.2} max={8} step={0.1}
            value={cr} onChange={(e) => setCr(+e.target.value)}
            className={sliderCls} aria-label="Baseline conversion rate"
          />
        </Field>

        <Field label="Average order value" value={usd0.format(aov)}>
          <input
            type="range" min={10} max={500} step={5}
            value={aov} onChange={(e) => setAov(+e.target.value)}
            className={sliderCls} aria-label="Average order value"
          />
        </Field>

        <Field
          label="Checkout recovered by native routing"
          hint="Your assumption, not ours. Set it where you think honest. Most teams model low single digits."
          value={`${lever}%`}
        >
          <input
            type="range" min={0} max={20} step={0.5}
            value={lever} onChange={(e) => setLever(+e.target.value)}
            className={sliderCls} aria-label="Checkout recovered by native routing"
          />
        </Field>
      </div>

      {/* Output */}
      <div className="flex flex-col justify-between rounded-2xl bg-brand-navy p-6 text-white">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-white/60">Indicative recovered checkout</p>
          <div className="mt-2 font-display text-4xl font-bold tabular-nums md:text-5xl">
            {usd0.format(recoveredMonthly)}
            <span className="text-lg font-medium text-white/60"> /mo</span>
          </div>
          <p className="mt-1 text-sm text-white/70">{usd0.format(recoveredAnnual)} a year</p>

          <dl className="mt-6 space-y-2 border-t border-white/15 pt-5 text-sm">
            <div className="flex justify-between"><dt className="text-white/60">Mobile sessions</dt><dd className="tabular-nums">{Math.round(mobileSessions).toLocaleString()}</dd></div>
            <div className="flex justify-between"><dt className="text-white/60">Baseline orders</dt><dd className="tabular-nums">{Math.round(baselineOrders).toLocaleString()}/mo</dd></div>
            <div className="flex justify-between"><dt className="text-white/60">Recovered orders</dt><dd className="tabular-nums text-primary">+{Math.round(recoveredOrders).toLocaleString()}/mo</dd></div>
          </dl>
        </div>
        <p className="mt-6 flex items-start gap-1.5 text-xs leading-snug text-white/55">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Indicative model, not a guarantee. Recovered orders = mobile sessions × conversion rate × your recovery assumption.
        </p>
      </div>
    </div>
  )
}

export function CheckoutLeakBadge() {
  return (
    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600 dark:text-emerald-400">
      <TrendingUp className="h-4 w-4" /> Free tool
    </span>
  )
}
